import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";

/* ====================================================== */
/* CompanyGate                                            */
/* ====================================================== */
/**
 * Handles:
 *  - No company → redirect to create
 *  - Multiple companies → enforce selection
 *  - Ensures active company exists before app loads
 */

const CompanyGate = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  const { companies, activeCompany, initializing } =
    useCompany();

  const location = useLocation();

  /* ====================================================== */
  /* Loading State                                          */
  /* ====================================================== */

  if (loading || initializing) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Loading workspace...
      </div>
    );
  }

  /* ====================================================== */
  /* Not Logged In (handled by RequireAuth already)         */
  /* ====================================================== */

  if (!isLoggedIn) return null;

  const hasCompanies =
    Array.isArray(companies) && companies.length > 0;

  const isCreatePage =
    location.pathname === "/company/create";

  const isSelectPage =
    location.pathname === "/select-company";

  /* ====================================================== */
  /* 🚨 NO COMPANY                                          */
  /* ====================================================== */

  if (!hasCompanies) {
    if (!isCreatePage) {
      return <Navigate to="/company/create" replace />;
    }
    return children;
  }

  /* ====================================================== */
  /* 🏢 MULTIPLE COMPANIES → MUST SELECT                    */
  /* ====================================================== */

  if (companies.length > 1 && !activeCompany) {
    if (!isSelectPage) {
      return <Navigate to="/select-company" replace />;
    }
    return children;
  }

  /* ====================================================== */
  /* 🧠 SAFETY: Active company missing (rare edge case)     */
  /* ====================================================== */

  if (!activeCompany) {
    return <Navigate to="/select-company" replace />;
  }

  /* ====================================================== */
  /* ✅ ALL GOOD                                            */
  /* ====================================================== */

  return children;
};

export default CompanyGate;